import { ProjectService, EnrichedRepriceRequest } from 'entities';
import { ProjectWithRepriceRequestParse } from '.';

export function parseProjectsRequest(
  repriceProjects: Record<string, EnrichedRepriceRequest>,
): ProjectWithRepriceRequestParse[] {
  return Object.values(repriceProjects).map(({ project, request, origin, ...requestData }) => {
    const fromInvoice = origin === 'INVOICE';

    const services = project.services
      .filter(({ id }) => (fromInvoice ? true : request[id]))
      .map(({ costs, id, name, outsourceConfig }) => {
        const countries = fromInvoice ? costs : request[id];

        const updatedCountries = Object.entries(costs)
          .filter(([country]) => countries[country])
          .map(([country, data]) => {
            const justification = fromInvoice ? 'n/a' : countries[country];

            return {
              country,
              justification,
              costs: data.map(({ history: _, ...rest }) => rest),
            };
          });

        return {
          costs: updatedCountries,
          name,
          id,
          outsourceConfig,
        };
      });

    return {
      ...project,

      services,

      currentServices: project.services,

      requestData: {
        ...requestData,

        origin,
      },
    };
  }) as ProjectWithRepriceRequestParse[];
}

export type RepriceService = Pick<
  ProjectService,
  'id' | 'name' | 'service' | 'costs' | 'outsourceConfig'
>;

export function unwrapServicesToAPIFormat(
  formatted: ProjectWithRepriceRequestParse['services'],
): RepriceService[] {
  return formatted.map(({ costs, id, service, name, outsourceConfig }) => {
    const costMapping = Object.fromEntries(costs.map(({ country, costs }) => [country, costs]));

    return {
      id,
      service,
      name,
      costs: costMapping,
      outsourceConfig,
    };
  });
}

export function mergeServices(
  control: RepriceService[],
  currentServices: ProjectService[],
): ProjectService[] {
  return currentServices.map((service) => {
    const controlService = control.find(({ id }) => id === service.id);

    return {
      ...service,

      ...controlService,

      costs: {
        ...service.costs,
        ...controlService?.costs,
      },
    };
  }) as ProjectService[];
}

export function updateServicesToRenderFormat(
  formatted: RepriceService[],
  initial: ProjectWithRepriceRequestParse['services'],
): ProjectWithRepriceRequestParse['services'] {
  return formatted.map(({ costs, id, name, outsourceConfig }) => {
    const baseService = initial.find((start) => start.id === id);

    const updatedCosts = Object.entries(costs).map(([country, data]) => {
      const initialCosts = baseService?.costs.find((cost) => cost.country === country);

      return {
        ...initialCosts,
        costs: data,
      };
    });

    return {
      id,
      costs: updatedCosts,
      name,
      outsourceConfig,
    };
  }) as ProjectWithRepriceRequestParse['services'];
}

export function verifyIfAnyValueWasChanged(
  services: RepriceService[],
  parsed: ProjectWithRepriceRequestParse['services'],
): boolean {
  return parsed.some(({ costs, id }) =>
    costs.some(({ country, costs }) =>
      costs.some(({ value, name }) =>
        services.find(
          (s) =>
            s.id === id &&
            s.costs[country].find((cost) => cost.name === name && cost.value !== value),
        ),
      ),
    ),
  );
}

export function extractOnlyChangedServices(
  repricedServices: ProjectWithRepriceRequestParse['services'],
  services: ProjectService[],
): ProjectWithRepriceRequestParse['services'] {
  const servicesWithChange = repricedServices.filter(({ costs, id }) =>
    costs.some(({ country, costs }) =>
      costs.some(({ value, name }) =>
        services.find(
          (s) =>
            s.id === id &&
            s.costs[country].find((cost) => cost.name === name && cost.value !== value),
        ),
      ),
    ),
  );

  return servicesWithChange.map((service) => ({
    ...service,

    costs: service.costs.filter(({ country, costs }) =>
      costs.some(({ value, name }) =>
        services.find(
          (s) =>
            s.id === service.id &&
            s.costs[country].find((cost) => cost.name === name && cost.value !== value),
        ),
      ),
    ),
  }));
}

export function verifyFilling(services: RepriceService[]): boolean {
  return services.every(({ costs }) => {
    const allValues = Object.values(costs).flat();

    return allValues.every((value) => Object.values(value).every(Boolean));
  });
}
